import { ComponentId } from 'pages/reporting/reports/rug/types'
import { GeckoComponentRect, Sectionable } from 'types'

/**
 * Creates GeckoComponentRect objects for each section in a Sectionable object.
 *
 * This function converts the sections of a Sectionable (e.g., tank shell courses or
 * boiler walls) into individual rectangular bounds that can be used for data analysis,
 * CDF filtering, or visualization clipping.
 *
 * The function handles both X-split and Y-split sectionables:
 * - For X-split sectionables (e.g., boiler walls): sections define X ranges,
 *   component bounds provide Y range
 * - For Y-split sectionables (e.g., tank shell courses): sections define Y ranges,
 *   component bounds provide X range
 *
 * Output rectangles preserve the coordinate units of the component bounds.
 * This is important for polar components, whose X coordinate is an angle
 * rather than a length.
 *
 * @param componentId - The ID of the component that the sections belong to
 * @param sectionable - The sectionable object containing sections and split configuration
 * @param componentBounds - The overall bounds of the component (optional, returns empty array if undefined)
 * @returns An array of GeckoComponentRect objects, one for each section, in the component's coordinate units. Preserves original section order.
 *
 * @example
 * ```typescript
 * // For a vertical tank shell with courses (Y-split)
 * const sectionable: Sectionable = {
 *   splitByX: false,
 *   sections: [
 *     { name: "Course 1", min: measurement(0, "m"), max: measurement(5, "m") },
 *     { name: "Course 2", min: measurement(5, "m"), max: measurement(10, "m") }
 *   ],
 *   // ... other properties
 * }
 *
 * const bounds = getBoundsFromSectionable("tank-123", sectionable, componentBounds)
 * // Returns two rectangles: one for each course spanning the full X range
 * ```
 */
export const getBoundsFromSectionable = (
  componentId: ComponentId,
  sectionable: Sectionable,
  componentBounds: GeckoComponentRect | undefined
): GeckoComponentRect[] => {
  if (!componentBounds) return []

  const sections = sectionable.sections
  const { x, y } = componentBounds

  return sections.map((section) => {
    const min = section.min
    const max = section.max
    return sectionable.splitByX
      ? GeckoComponentRect.CreateFromXYs(
          componentId,
          min.toNumber(x.unit),
          y.min.toNumber(y.unit),
          max.toNumber(x.unit),
          y.max.toNumber(y.unit),
          x.unit,
          y.unit
        )
      : GeckoComponentRect.CreateFromXYs(
          componentId,
          x.min.toNumber(x.unit),
          min.toNumber(y.unit),
          x.max.toNumber(x.unit),
          max.toNumber(y.unit),
          x.unit,
          y.unit
        )
  })
}
